import { Injectable } from '@angular/core';
import { Observable, of, firstValueFrom } from 'rxjs';
import { ApiHandlerService } from '../../../../shared/services/api-handler/api-handler.service';
import { AUTHX_APP_ID, AUTHX_PERMISSIONS_VERSION } from '../../../../core/constants/permissions.constants';
import { IPermissionFinal, BitWiseParser, IPermission } from '../../../../../assets/helper/bitwiseParser';
import { DataShareServiceService } from '../../../../shared/services/data-share-service.service';

interface UserPreferences {
    version?: string;
    permissions?: IPermission[];
    permission_seq?: number[];
    [key: string]: unknown;
}

const BITWISE_PARSER_VERSION = 'v1';

/**
 * Service responsible for handling permission operations including fetching, caching, and bitwise parsing
 */
@Injectable({
    providedIn: 'root',
})
export class PermissionService {
    private bitwiseCache: IPermissionFinal[] | null = null;
    private permissionsCache: UserPreferences | null = null;
    private dbName = 'PermissionsDB';
    private storeName = 'user_permissions';
    private dbVersion = 1;

    constructor(
        private apiHandler: ApiHandlerService,
        private dataShareService: DataShareServiceService
    ) { }

    /**
     * Get permissions from server
     * @returns Observable resolving to permissions data
     */
    getPermissions(userId: string, version: string): Observable<any> {
        try {
            return this.apiHandler.fetchData(
                `/api/authX/v1/permissions/app/${AUTHX_APP_ID}`,
                'GET',
                undefined,
                false,
                0,
                false
            )
        } catch (error) {
            console.error('Error fetching permissions:', error);
            return of(null);
        }
    }

    /**
     * Get permissions and process them with bitwise parser
     * @returns Promise resolving to parsed bitwise permissions
     */
    async getPermissionsBitwise(): Promise<IPermissionFinal[]> {
        const userData = this.dataShareService.getUserData();
        if (!userData || !userData.user_id) {
            throw new Error('User data not found');
        }

        // Try to retrieve from IndexedDB first
        try {
            const cachedPermissions = await this.retrievePermissions(userData.user_id);
            if (cachedPermissions) {
                console.log('Loaded permissions from IndexedDB');
                this.bitwiseCache = cachedPermissions;
                return cachedPermissions;
            }
        } catch (error) {
            console.warn('Failed to load permissions from IndexedDB:', error);
        }

        // Fetch permissions if not cached
        if (!this.permissionsCache) {
            // Assuming version 1 for now, or fetch from config
            const response = await firstValueFrom(this.getPermissions(userData.user_id, AUTHX_PERMISSIONS_VERSION));
            this.permissionsCache = response?.data || response;
        }

        const userPreferences = this.permissionsCache;

        if (!userPreferences?.permissions || !userPreferences?.permission_seq) {
            throw new Error('Invalid permission data structure');
        }

        const parser = new BitWiseParser(BITWISE_PARSER_VERSION);
        const parserObj = parser.getParser();
        parserObj.permissionList = userPreferences.permissions;
        parserObj.permissionSequence = userPreferences.permission_seq;

        // Use the permission string from user data
        const userPermissionCode = userData.permission || '';
        parserObj.permissionCode = userPermissionCode;

        const result = parserObj.parsePermission();
        this.bitwiseCache = result;

        // Store parsed permissions in IndexedDB
        try {
            await this.storePermissions(userData.user_id, result);
        } catch (error) {
            console.warn('Failed to store permissions in IndexedDB:', error);
        }

        return result;
    }

    private async initDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onerror = () => {
                reject('Error opening IndexedDB');
            };

            request.onsuccess = (event: any) => {
                resolve(event.target.result);
            };

            request.onupgradeneeded = (event: any) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, { keyPath: 'userId' });
                }
            };
        });
    }

    private async storePermissions(userId: string, permissions: IPermissionFinal[]): Promise<void> {
        const db = await this.initDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.put({ userId, permissions });

            request.onsuccess = () => resolve();
            request.onerror = () => reject('Error storing permissions');
        });
    }

    private async retrievePermissions(userId: string): Promise<IPermissionFinal[] | null> {
        const db = await this.initDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.get(userId);

            request.onsuccess = (event: any) => {
                const result = event.target.result;
                resolve(result ? result.permissions : null);
            };
            request.onerror = () => reject('Error retrieving permissions');
        });
    }

    /**
     * Clear all caches
     */
    clearCache(): void {
        this.permissionsCache = null;
        this.bitwiseCache = null;
        this.initDB().then((db) => {
            const transaction = db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            store.clear();
        });
    }

    /**
     * Get cached bitwise permissions (if available)
     */
    getCachedBitwisePermissions(): IPermissionFinal[] | null {
        return this.bitwiseCache;
    }

    /**
     * Get permission by name - fetches from API if not cached
     */
    async getPermissionByName(name: string): Promise<boolean> {
        // If not in memory cache, try to load from IndexedDB or fetch from API
        if (!this.bitwiseCache || !Array.isArray(this.bitwiseCache)) {
            try {
                // This will check IndexedDB first, then fetch from API if needed
                await this.getPermissionsBitwise();
            } catch (error) {
                console.warn('Failed to fetch permissions:', error);
                return false;
            }
        }

        if (!this.bitwiseCache) {
            return false;
        }

        const permission = this.bitwiseCache.find(
            (perm: IPermissionFinal) => perm.permissionName === name,
        );
        return permission ? (permission.isEnabled ?? false) : false;
    }
}
